import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import { RESIGN_POLICIES, publishChoiceProblems } from '../../lib/documentVersion';

// ============================================================================
// "Publish a new version" — the choice every publish has to make (brief A §1)
// ============================================================================
// Publishing a document that suppliers have already signed is a decision about
// THEM, not just about the content. This dialog refuses to let that decision be
// implicit: pick what happens to the suppliers on the previous version, and see
// the count it applies to before you commit.
//
//   A  Nothing changes      — typo/formatting. Signatures stay valid.
//   B  Sign again by a date — the default for a real change. They stay
//                             compliant until the deadline, then they don't.
//   C  Sign again now       — regulation-forced only. Says plainly how many
//                             suppliers it takes out of play today.
//
// Reason is required for B and C (the supplier reads it verbatim when they
// re-sign); B also requires a future deadline. Same rules as the DB CHECK, via
// publishChoiceProblems.
// ============================================================================

const FONT = "font-['Metropolis']";

const todayIso = () => new Date().toISOString().slice(0, 10);

// Default deadline offer: 30 days out. A deadline still has to be chosen
// deliberately — this is a starting point, not a silent default.
const defaultDeadline = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

function suppliersPhrase(n) {
  if (n === 0) return 'No suppliers have signed this version yet';
  return n === 1 ? '1 supplier has signed this version' : `${n} suppliers have signed this version`;
}

function OptionCard({ id, checked, onSelect, letter, title, effect, tone, children }) {
  const borderColour = checked
    ? (tone === 'red' ? '#E0290A' : tone === 'amber' ? '#E89A2E' : '#92D400')
    : '#E6EADB';
  return (
    <label
      htmlFor={id}
      className={`block rounded-xl border-[1.5px] px-4 py-3.5 mb-2.5 cursor-pointer transition-colors ${checked ? 'bg-[#F7F9F0]' : 'bg-white hover:border-[#DADCCD]'}`}
      style={{ borderColor: borderColour }}
    >
      <div className="flex items-start gap-3">
        <input
          type="radio"
          id={id}
          name="resign-policy"
          className="mt-1 flex-shrink-0 accent-[#009159]"
          checked={checked}
          onChange={onSelect}
        />
        <div className="min-w-0 flex-1">
          <div className={`${FONT} text-[13px] font-extrabold text-ink`}>
            <span className="text-[#51534A] mr-1.5">{letter}</span>{title}
          </div>
          <div className={`${FONT} text-[12px] text-[#51534A] leading-snug mt-1`}>{effect}</div>
          {checked && children}
        </div>
      </div>
    </label>
  );
}

export default function PublishVersionDialog({
  open,
  onClose,
  onConfirm,
  documentName,
  liveVersion,
  nextVersion,
  affectedCount = 0,
  countLoading = false,
  busy = false,
  error = null,
}) {
  const [policy, setPolicy] = useState(RESIGN_POLICIES.DEADLINE);
  const [changeReason, setChangeReason] = useState('');
  const [resignDeadline, setResignDeadline] = useState(defaultDeadline);
  const [attempted, setAttempted] = useState(false);

  // Fresh decision every time the dialog opens — a reason left over from a
  // previous publish must never ride along on the next one.
  useEffect(() => {
    if (!open) return;
    setPolicy(RESIGN_POLICIES.DEADLINE);
    setChangeReason('');
    setResignDeadline(defaultDeadline());
    setAttempted(false);
  }, [open]);

  const choice = useMemo(() => ({
    resignPolicy: policy,
    changeReason,
    resignDeadline: policy === RESIGN_POLICIES.DEADLINE ? resignDeadline : null,
  }), [policy, changeReason, resignDeadline]);

  const problems = useMemo(
    () => publishChoiceProblems(choice, { todayIso: todayIso() }),
    [choice],
  );

  const countLabel = countLoading ? 'Checking who has signed…' : suppliersPhrase(affectedCount);

  const submit = () => {
    setAttempted(true);
    if (problems.length > 0 || busy) return;
    onConfirm(choice);
  };

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} maxWidth={620} ariaLabel="Publish a new version">
      <Modal.Header
        eyebrow={liveVersion ? `v${liveVersion} → v${nextVersion}` : 'Publish'}
        title="What happens to suppliers who signed the last version?"
        sub={documentName}
        onClose={busy ? undefined : onClose}
      />
      <Modal.Body>
        <div
          className={`${FONT} text-[12px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-4`}
          aria-live="polite"
        >
          {countLabel}
        </div>

        <fieldset>
          <legend className="sr-only">What happens to suppliers who signed the previous version</legend>

          <OptionCard
            id="policy-none"
            letter="A"
            checked={policy === RESIGN_POLICIES.NONE}
            onSelect={() => setPolicy(RESIGN_POLICIES.NONE)}
            title="Nothing changes"
            tone="green"
            effect={affectedCount > 0
              ? `For a typo or formatting fix. The ${affectedCount === 1 ? 'signature' : `${affectedCount} signatures`} on the previous version stay valid; only suppliers who haven't signed yet see the new one. No emails, no status changes.`
              : 'For a typo or formatting fix. Nobody is asked to do anything.'}
          />

          <OptionCard
            id="policy-deadline"
            letter="B"
            checked={policy === RESIGN_POLICIES.DEADLINE}
            onSelect={() => setPolicy(RESIGN_POLICIES.DEADLINE)}
            title="Sign again, by a deadline"
            tone="amber"
            effect={affectedCount > 0
              ? `The usual choice for a real change. ${affectedCount === 1 ? 'That supplier is' : `Those ${affectedCount} suppliers are`} asked to sign again and stay compliant until the deadline — after it, they're not compliant and can't be awarded new work.`
              : 'The usual choice for a real change. Nobody has signed this version yet, so nobody is chased.'}
          >
            <div className="mt-3">
              <label htmlFor="resign-deadline" className={`${FONT} block text-[11px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-1.5`}>
                Sign again by
              </label>
              <input
                id="resign-deadline"
                type="date"
                value={resignDeadline || ''}
                min={todayIso()}
                onChange={e => setResignDeadline(e.target.value)}
                className={`${FONT} bg-white border border-line rounded-lg text-ink px-3 py-2 text-sm focus:outline-none focus:border-[#92D400]`}
              />
              <p className={`${FONT} text-[11px] text-[#7C7F6F] mt-1.5`}>
                Reminders go out 14, 7 and 1 day before.
              </p>
            </div>
          </OptionCard>

          <OptionCard
            id="policy-immediate"
            letter="C"
            checked={policy === RESIGN_POLICIES.IMMEDIATE}
            onSelect={() => setPolicy(RESIGN_POLICIES.IMMEDIATE)}
            title="Sign again, immediately"
            tone="red"
            effect="Only when a regulation forces it — there is no grace period."
          >
            {affectedCount > 0 && (
              <div
                className={`${FONT} mt-3 rounded-lg border-[1.5px] border-[#E0290A]/40 bg-[#E0290A]/[0.06] px-3 py-2.5 text-[12px] font-bold text-[#B02309]`}
                role="alert"
              >
                This takes {affectedCount} supplier{affectedCount === 1 ? '' : 's'} out of play today — no new work can be awarded to {affectedCount === 1 ? 'them' : 'any of them'} until they sign the new version.
              </div>
            )}
          </OptionCard>
        </fieldset>

        {policy !== RESIGN_POLICIES.NONE && (
          <div className="mt-4">
            <label htmlFor="change-reason" className={`${FONT} block text-[11px] font-bold uppercase tracking-[0.14em] text-[#51534A] mb-1.5`}>
              What changed?
            </label>
            <textarea
              id="change-reason"
              rows={3}
              value={changeReason}
              onChange={e => setChangeReason(e.target.value)}
              placeholder="e.g. Updated the insurance clause to £10m cover"
              className={`${FONT} w-full bg-white border border-line rounded-lg text-ink px-3 py-2 text-sm focus:outline-none focus:border-[#92D400]`}
            />
            <p className={`${FONT} text-[11px] text-[#7C7F6F] mt-1.5`}>
              The supplier reads this word for word when they're asked to sign again. It stays on the version permanently.
            </p>
          </div>
        )}

        {(attempted && problems.length > 0) && (
          <ul className={`${FONT} mt-4 text-[12px] text-[#B02309] flex flex-col gap-1`} aria-live="polite">
            {problems.map((p, i) => <li key={i}>• {p}</li>)}
          </ul>
        )}

        {error && (
          <p className={`${FONT} mt-4 text-[12px] text-[#B02309]`} role="alert">{error}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between gap-3">
          <p className={`${FONT} text-[11px] text-[#7C7F6F]`}>
            Publishing can't be undone — correct it by publishing again.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className={`${FONT} text-[13px] font-bold text-[#51534A] px-4 py-2.5 rounded-lg border border-line bg-white hover:border-[#DADCCD] disabled:opacity-50`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className={`${FONT} text-[13px] font-bold text-white px-5 py-2.5 rounded-lg disabled:opacity-60 ${policy === RESIGN_POLICIES.IMMEDIATE ? 'bg-[#E0290A] hover:brightness-105' : 'bg-[#009159] hover:brightness-105'}`}
            >
              {busy ? 'Publishing…' : `Publish v${nextVersion}`}
            </button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
